// Pure helpers for the "renew the whole family in one payment" basket.
// No React, no API: keeps the renewal rules unit-testable outside the screen.
import type { MemberPlanCatalogEntry } from '../../api/payments';
import { getAllowedTermsForMemberPlan, getMemberOffers } from '../../components/billing/billingUtils';

export const RENEW_WINDOW_DAYS = 30;

export type RenewableMembership = {
  status?: string;
  end_date?: string | null;
} | null | undefined;

export type PendingRenewal = { planId: string; termMonths: number };
export type PendingRenewals = Record<string, PendingRenewal>;

const MS_PER_DAY = 86400000;

// Calendar days are the user's LOCAL days: "ends 2026-10-16" means that date on
// the member's own calendar, whatever the time of day "today" is evaluated at.
function localDayNumber(y: number, m: number, d: number): number {
  // Date.UTC on local y/m/d gives a DST-proof whole-day index.
  return Math.floor(Date.UTC(y, m, d) / MS_PER_DAY);
}

// A plain 'YYYY-MM-DD' is a local calendar date. Anything with a time/zone is an
// instant: parse it and let the local getters in daysUntil pick the local day.
function parseLocalDate(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const d = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Whole local calendar days from `today` until `endDate`; negative when already past.
function daysUntil(endDate: string, today: Date): number | null {
  const end = parseLocalDate(endDate);
  if (!end) return null;
  return localDayNumber(end.getFullYear(), end.getMonth(), end.getDate())
    - localDayNumber(today.getFullYear(), today.getMonth(), today.getDate());
}

// Expired/cancelled memberships can always be renewed. Active/paused ones can be
// renewed early once the end date is within `windowDays` (inclusive) or already past.
export function isRenewable(
  membership: RenewableMembership,
  today: Date,
  windowDays: number = RENEW_WINDOW_DAYS,
): boolean {
  if (!membership) return false;
  const status = membership.status;
  if (status === 'expired' || status === 'cancelled') return true;
  if (status !== 'active' && status !== 'paused') return false;
  if (!membership.end_date) return false;
  const days = daysUntil(membership.end_date, today);
  if (days === null) return false;
  return days <= windowDays;
}

export function isEarlyRenewal(
  membership: RenewableMembership,
  today: Date,
  windowDays: number = RENEW_WINDOW_DAYS,
): boolean {
  if (!membership) return false;
  const status = membership.status;
  if (status !== 'active' && status !== 'paused') return false;
  return isRenewable(membership, today, windowDays);
}

export function buildRenewalItems(
  pending: PendingRenewals,
): { user_id: string; plan_id: string; term_months: number }[] {
  return Object.entries(pending).map(([user_id, r]) => ({
    user_id,
    plan_id: r.planId,
    term_months: r.termMonths,
  }));
}

export function basketTotal(rows: { finalAmount: number }[]): number {
  return rows.reduce((acc, r) => acc + r.finalAmount, 0);
}

// A queued renewal is only valid against CURRENT data: the member must still be
// renewable (a member with no membership at all counts as "add plan" and is fine),
// the plan must still be offered, and the frozen term must still be allowed.
export function isRenewalTermValid(
  planExists: boolean,
  catalogEntries: MemberPlanCatalogEntry[],
  userId: string,
  planId: string,
  termMonths: number,
): boolean {
  if (!planExists) return false;
  // The plan must be offered to THIS member: getAllowedTermsForMemberPlan falls back
  // to [3, 6, 12] when it finds no offer, which would let a withdrawn plan through.
  const offers = getMemberOffers(catalogEntries, userId);
  if (!offers.some((o) => o.plan_id === planId)) return false;
  return getAllowedTermsForMemberPlan(offers, planId).includes(termMonths);
}

export function validateRenewals(
  pending: PendingRenewals,
  familyMemberships: { user_id: string; membership?: RenewableMembership }[] | null | undefined,
  plans: { id: string }[] | null | undefined,
  catalogEntries: MemberPlanCatalogEntry[],
  today: Date,
  windowDays: number = RENEW_WINDOW_DAYS,
): { valid: PendingRenewals; dropped: string[] } {
  const valid: PendingRenewals = {};
  const dropped: string[] = [];
  const planIds = new Set((plans ?? []).map((p) => p.id));
  for (const [userId, r] of Object.entries(pending)) {
    const fm = familyMemberships?.find((f) => f.user_id === userId);
    const memberOk = !!fm && (!fm.membership || isRenewable(fm.membership, today, windowDays));
    const termOk = isRenewalTermValid(planIds.has(r.planId), catalogEntries, userId, r.planId, r.termMonths);
    if (memberOk && termOk) valid[userId] = r;
    else dropped.push(userId);
  }
  return { valid, dropped };
}
